import { useEffect, useRef } from "react";
import { noteForecastOpen, takeForecastOpen } from "../lib/telemetry/forecastOpen.js";
import { telemetryEntrySource } from "../services/p2Telemetry.js";
import { beginIntent } from "../lib/telemetry/instrument";
import { recentOrigin } from "../lib/telemetry/recentControl.js";

/**
 * `forecast.open` at the focus transition (P2 design §4.6): when the focused forecast changes
 * and an entry point noted how (lib/telemetry/forecastOpen.js), open the intent with that
 * `from` and end it `ok` with `render_ms` once the new forecast has painted. A newer open
 * before that supersedes it (the emitter's rule for UI-local intents).
 *
 * @param {{activeId: string|null|undefined}} deps the focused run's client id
 */
export function useForecastOpenIntent({ activeId }) {
  const lastRef = useRef(activeId ?? null);
  useEffect(() => {
    const id = activeId ?? null;
    if (id === lastRef.current) return;
    lastRef.current = id;
    const from = takeForecastOpen();
    if (!id || !from) return;
    // A press that opens a forecast names its control (a chat history heading, a phone card);
    // app-initiated opens and deep links never borrow a press.
    const pressed = USER_FROM.has(from) ? recentOrigin("forecast.open") : null;
    const intent = beginIntent(
      "forecast.open",
      { forecast_id: id, from },
      { origin: pressed ?? originOf(from) },
    );
    if (!intent.id) return;
    // Two frames after the commit that focused it: the forecast has painted.
    const raf =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame
        : (fn) => setTimeout(fn, 16);
    raf(() =>
      raf(() => {
        intent.mark("render_ms");
        intent.end("ok");
      }),
    );
  }, [activeId]);
}

/** The `from` values a user's press starts (the others are deep links and app-initiated). */
const USER_FROM = new Set(["manager", "rail", "chat", "featured", "phone"]);

/** The intent `origin` for a `from`: the row control, the deep link, or app-initiated work. */
function originOf(from) {
  if (from === "manager" || from === "rail") return "forecast-manager.row";
  if (from === "link" || from === "alert") return "deeplink";
  if (from === "chat") return "chat";
  if (from === "featured") return "ask-hero.featured";
  if (from === "phone") return "phone.forecast-card";
  return "auto";
}

/** A deep link is about to focus a forecast: `alert` when the page came from an alert email. */
export function noteDeepLinkOpen() {
  noteForecastOpen(telemetryEntrySource() === "alert" ? "alert" : "link");
}

/**
 * A forecast-manager row is about to focus a forecast. An origin noted just before wins: a deep
 * link, or a caller that reuses the row handler (the chat history, a featured card) and noted its
 * own first.
 */
export function noteRowOpen() {
  noteForecastOpen("rail", { keep: true });
}

/** A forecast in the phone layout's list is about to be focused (a deep link's origin wins). */
export function notePhoneOpen() {
  noteForecastOpen("phone", { keep: true });
}
